import { extendTheme, defineStyle } from '@chakra-ui/react';

/**
 * ──────────────────────────────────────────────────────────────────────────────
 * DESIGN TOKENS & CONFIGURATION
 * Professional Enterprise Agro Marketplace Design System
 * ──────────────────────────────────────────────────────────────────────────────
 */

// CONFIG
const config = {
  initialColorMode: 'light',
  useSystemColorMode: false,
} as const;

// ─── SPACING SYSTEM (8px Grid) ──────────────────────────────────────────────
const spacingToken = {
  xs: '4px',    // 0.5 * grid
  sm: '8px',    // 1 * grid
  md: '16px',   // 2 * grid
  lg: '24px',   // 3 * grid
  xl: '32px',   // 4 * grid
  '2xl': '40px', // 5 * grid
  '3xl': '48px', // 6 * grid
  '4xl': '64px', // 8 * grid
};

// ─── AGRICULTURAL COLOR PALETTE – Grounded & High-Contrast ──────────────────
const colors = {
  // Primary: Deep Forest Green (agricultural, trustworthy)
  primary: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#1a5e3f',  // Deep forest green (brand primary)
    600: '#15803d',
    700: '#166534',
    800: '#166534',
    900: '#14532d',
  },
  
  // Secondary: Earthy Natural Brown (soil, stability)
  secondary: {
    50: '#faf8f3',
    100: '#f2ece0',
    200: '#e8dcc7',
    300: '#d8c7a8',
    400: '#c9ad81',
    500: '#8b7355', // Earthy brown
    600: '#6d5a47',
    700: '#5a4a39',
    800: '#463a2d',
    900: '#322622',
  },
  
  // Accent: Autumn Gold (harvest, growth)
  accent: {
    50: '#faf6f0',
    100: '#f3edde',
    200: '#e7dbb5',
    300: '#dcc980',
    400: '#d4b756',
    500: '#b8860b', // Harvest gold
    600: '#9a7109',
    700: '#7a5a08',
    800: '#5a4407',
    900: '#403005',
  },
  
  // Neutral: High-contrast charcoal (readability for outdoor use)
  neutral: {
    50: '#f9f9f9',
    100: '#f0f0f0',
    200: '#e8e8e8',
    300: '#d0d0d0',
    400: '#a8a8a8',
    500: '#6b6b6b',
    600: '#505050',
    700: '#323232', // High-contrast charcoal
    800: '#1a1a1a',
    900: '#0d0d0d',
  },
  
  // Semantic Colors
  success: '#1a7551',  // Deep teal for success
  warning: '#d97706',  // Amber for warnings
  danger: '#c41e3a',   // Deep red for errors
  info: '#0e7490',     // Teal for info
};

// ─── SHADOWS (Subtle, Realistic) ────────────────────────────────────────────
const shadows = {
  'sm-subtle': '0 2px 4px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
  'md-subtle': '0 4px 8px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
  'lg-subtle': '0 8px 16px rgba(0, 0, 0, 0.10), 0 4px 8px rgba(0, 0, 0, 0.06)',
  'xl-subtle': '0 16px 32px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08)',
  'hover': '0 12px 24px rgba(0, 0, 0, 0.12), 0 6px 12px rgba(0, 0, 0, 0.08)',
  'focus': '0 0 0 3px rgba(26, 94, 63, 0.1), 0 4px 8px rgba(0, 0, 0, 0.08)',
};

// ─── TYPOGRAPHY ─────────────────────────────────────────────────────────────
const fonts = {
  // Production fonts: Inter (UI), DM Sans (Headings)
  heading: `'DM Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif`,
  body: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif`,
};

// ─── BRAND COMPONENT VARIANTS ───────────────────────────────────────────────

// Primary Button (Call-to-action)
const primaryButton = defineStyle({
  bg: 'primary.500',
  color: 'white',
  fontWeight: '600',
  fontSize: '14px',
  borderRadius: '8px',
  padding: '12px 20px',
  border: 'none',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: shadows['sm-subtle'],
  _hover: {
    bg: 'primary.600',
    boxShadow: shadows['hover'],
    transform: 'translateY(-1px)',
  },
  _active: {
    bg: 'primary.700',
    transform: 'translateY(0)',
  },
  _disabled: {
    bg: 'neutral.300',
    color: 'neutral.500',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
});

// Secondary Button (Secondary actions)
const secondaryButton = defineStyle({
  bg: 'white',
  color: 'primary.500',
  fontWeight: '600',
  fontSize: '14px',
  borderRadius: '8px',
  padding: '12px 20px',
  border: '2px solid',
  borderColor: 'primary.500',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: shadows['sm-subtle'],
  _hover: {
    bg: 'primary.50',
    borderColor: 'primary.600',
    boxShadow: shadows['hover'],
  },
  _active: {
    bg: 'primary.100',
  },
  _disabled: {
    borderColor: 'neutral.300',
    color: 'neutral.500',
    cursor: 'not-allowed',
  },
});

// Ghost Button (Tertiary, minimal)
const ghostButton = defineStyle({
  bg: 'transparent',
  color: 'neutral.700',
  fontWeight: '500',
  fontSize: '14px',
  borderRadius: '8px',
  padding: '10px 16px',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  _hover: {
    bg: 'neutral.100',
  },
  _active: {
    bg: 'neutral.200',
  },
});

// ─── THEME CONFIGURATION ───────────────────────────────────────────────────
const theme = extendTheme({
  config,
  colors,
  space: spacingToken,
  shadows,
  fonts,
  
  // Component overrides
  components: {
    Button: {
      variants: {
        primary: primaryButton,
        secondary: secondaryButton,
        ghost: ghostButton,
      },
      defaultProps: {
        variant: 'primary',
      },
    },
    
    Link: {
      baseStyle: {
        _hover: {
          textDecoration: 'none',
        },
        _focus: {
          outline: 'none',
          boxShadow: shadows['focus'],
        }
      },
    },
    
    // Input field styling
    Input: {
      baseStyle: {
        field: {
          borderRadius: '8px',
          borderColor: 'neutral.200',
          fontSize: '14px',
          fontWeight: '500',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          _focus: {
            borderColor: 'primary.500',
            boxShadow: shadows['focus'],
          },
          _placeholder: {
            color: 'neutral.400',
          },
        },
      },
    },
  },
  
  styles: {
    global: {
      body: {
        bg: 'neutral.50',
        color: 'neutral.800',
        fontFamily: fonts.body,
      },
      html: {
        scrollBehavior: 'smooth',
      },
    },
  },
});

export default theme;
export { colors, shadows, spacingToken, fonts };